import { ApolloClient, ApolloLink, split } from "@apollo/client";
import { InMemoryCache, NormalizedCacheObject } from "@apollo/client/cache";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { HttpLink } from "@apollo/client/link/http";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import possibleTypes from "__generated__/possible-types.json";
import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
} from "aws-amplify/auth";
import { getAuthorizationTokenJson } from "common/auth/Auth";
import { config } from "common/config";
import { createClient as createWsClient } from "graphql-ws";

/**
 * In development, delay API calls to simplify testing of loading states.
 */
function createDelayLink(): ApolloLink {
  const getDelay = () => {
    const apiDelay = localStorage.getItem("apiDelay");
    return apiDelay ? parseInt(apiDelay, 10) : 0;
  };

  console.info(
    `%cIn the current environment, API calls are delayed by ${getDelay()} ms ` +
      "to cause more visible loading states.\n\n" +
      "This can be controlled by:\n\n" +
      '  localStorage.setItem("apiDelay", delayTimeInMs)\n\n' +
      "Set to 0 to disable",
    "color: blue",
  );

  return setContext(async (request) => {
    const delayTime = getDelay();

    if (delayTime > 0) {
      console.debug(
        `Delaying operation ${
          request.operationName ?? "UNKNOWN"
        } by ${delayTime} ms`,
      );
      await new Promise((resolve) => window.setTimeout(resolve, delayTime));
    }
  });
}

async function getHeaders() {
  return {
    ...(await getAuthorizationTokenJson()),
  };
}

export function createClient(): ApolloClient<NormalizedCacheObject> {
  // Set up our apollo-client to point at the server we created
  // this can be local or a remote endpoint
  const cache: InMemoryCache = new InMemoryCache({
    possibleTypes,
  });

  const httpLink: HttpLink = new HttpLink({
    uri: `${config.apiUrl}/graphql`,
  });

  let activeSocket, timedOut;
  const wsLink = new GraphQLWsLink(
    createWsClient({
      url: `${config.wsUrl}/ws`,
      lazy: true,
      keepAlive: 10_000,
      lazyCloseTimeout: 10_000,
      connectionParams: async () => {
        return { headers: await getHeaders() };
      },
      on: {
        connected: (socket) => (activeSocket = socket),
        error: (err) => {
          if (
            typeof err === "object" &&
            err !== null &&
            "message" in err &&
            err.message.includes("401")
          ) {
            void signInWithRedirect();
          } else {
            console.log(err);
          }
        },
        pong: (received) => {
          if (received) clearTimeout(timedOut);
        },
      },
    }),
  );

  const authLink: ApolloLink = setContext(async () => {
    return {
      headers: await getHeaders(),
    };
  });

  const errorLink = onError(({ networkError }) => {
    const sendToLogin = () => signInWithRedirect();
    // Send user to login page if certain criteria is met (user is disabled, refresh token not valid, etc.)
    if (
      networkError &&
      "statusCode" in networkError &&
      networkError.statusCode === 401
    ) {
      async () => {
        try {
          const user = await getCurrentUser();
          if (user != null) {
            await fetchAuthSession({ forceRefresh: true })
              .then(() => void client.resetStore())
              .catch(sendToLogin);
          }
        } catch (err) {
          void sendToLogin();
          console.log(err);
        }
      };
    }
  });

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    HttpLink.from(
      config.allowApiDelay
        ? [createDelayLink(), authLink, errorLink, httpLink]
        : [authLink, errorLink, httpLink],
    ),
  );

  const client = new ApolloClient({
    cache,
    link: splitLink,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first",
      },
      query: {
        fetchPolicy: "cache-first",
      },
    },
  });

  return client;
}
